import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import { rentalsApi } from '../lib/api'

type PayoutStatus = 'PAID_OUT' | 'PENDING' | 'UPCOMING'

interface EarningsRow {
  id: string
  itemTitle: string
  renterName: string
  startDate: string | null
  endDate: string | null
  paidAt: string | null
  status: string
  payoutStatus: PayoutStatus
  amount: number
  payoutAt: string | null
  blockedReason: string | null
}

interface EarningsSummary {
  total: number
  paidOut: number
  pending: number
  upcoming: number
}

const PAYOUT_BADGE: Record<PayoutStatus, { label: string; className: string }> = {
  PAID_OUT: { label: 'Paid out', className: 'bg-green-100 text-green-700' },
  PENDING: { label: 'Pending', className: 'bg-amber-100 text-amber-700' },
  UPCOMING: { label: 'Upcoming', className: 'bg-slate-100 text-slate-600' },
}

const usd = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

// What the payout column says under the badge, per state.
function payoutDetail(row: EarningsRow): string {
  if (row.payoutStatus === 'PAID_OUT') return `Sent ${formatDate(row.payoutAt)}`
  if (row.payoutStatus === 'PENDING') return row.blockedReason || 'Due — sending shortly'
  return 'Sent when the item is picked up'
}

function SummaryCard({ label, value, hint, accent }: { label: string; value: number; hint: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-xl p-5 border shadow-sm ${
        accent ? 'bg-[var(--forest-green)] border-[var(--forest-green)] text-white' : 'bg-white border-[var(--border)]'
      }`}
    >
      <div className={`text-sm font-medium ${accent ? 'text-white/80' : 'text-[var(--text-secondary)]'}`}>{label}</div>
      <div className={`text-xl sm:text-3xl font-bold mt-1 ${accent ? '' : 'text-[var(--text-primary)]'}`}>{usd(value)}</div>
      <div className={`text-xs mt-1 ${accent ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>{hint}</div>
    </div>
  )
}

export default function Earnings() {
  const [summary, setSummary] = useState<EarningsSummary | null>(null)
  const [rows, setRows] = useState<EarningsRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    rentalsApi
      .getEarnings()
      .then((response) => {
        if (cancelled) return
        setSummary(response.data.data.summary)
        setRows(response.data.data.rows)
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load your earnings. Please try again.")
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const blockedCount = rows.filter((row) => row.payoutStatus === 'PENDING').length

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />

      <main className="min-h-[calc(100vh-var(--navbar-height))]">
        <div className="container mx-auto px-4 py-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">Earnings</h1>
            <p className="text-[var(--text-secondary)] mt-1">
              What your rentals have earned, and when it reaches your Stripe account
            </p>
          </div>

          {isLoading && (
            <div className="flex justify-center py-16" role="status" aria-label="Loading earnings">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--forest-green)]" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-4 rounded-xl" role="alert">
              {error}
            </div>
          )}

          {summary && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <SummaryCard accent label="Total earned" value={summary.total} hint="Paid out + pending" />
                <SummaryCard label="Paid out" value={summary.paidOut} hint="Sent to your Stripe account" />
                <SummaryCard label="Pending" value={summary.pending} hint="Earned, not sent yet" />
                <SummaryCard label="Upcoming" value={summary.upcoming} hint="Paid for, not picked up yet" />
              </div>

              {blockedCount > 0 && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl mb-6 text-sm">
                  {blockedCount === 1 ? '1 payout is' : `${blockedCount} payouts are`} waiting to be sent.{' '}
                  <Link to="/profile?tab=payouts" className="font-semibold underline">
                    Check your Stripe connection
                  </Link>
                </div>
              )}

              {rows.length === 0 ? (
                <div className="bg-white rounded-xl border border-[var(--border)] p-10 text-center">
                  <i className="mdi mdi-cash-multiple text-4xl text-[var(--text-muted)]" aria-hidden="true"></i>
                  <p className="mt-3 font-semibold text-[var(--text-primary)]">No earnings yet</p>
                  <p className="text-sm text-[var(--text-secondary)] mt-1">
                    Once someone pays to rent one of your items, it will show up here.
                  </p>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-[var(--border)] overflow-hidden" role="table" aria-label="Earnings by rental">
                  <div
                    className="hidden sm:grid grid-cols-12 gap-4 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)] border-b border-[var(--border)]"
                    role="row"
                  >
                    <div className="col-span-4" role="columnheader">Rental</div>
                    <div className="col-span-3" role="columnheader">Dates</div>
                    <div className="col-span-2 text-right" role="columnheader">Amount</div>
                    <div className="col-span-3" role="columnheader">Payout</div>
                  </div>

                  {rows.map((row) => {
                    const badge = PAYOUT_BADGE[row.payoutStatus]
                    return (
                      <Link
                        key={row.id}
                        to={`/rental/${row.id}`}
                        role="row"
                        className="grid grid-cols-2 sm:grid-cols-12 gap-x-4 gap-y-2 px-5 py-4 border-b last:border-b-0 border-[var(--border)] hover:bg-[var(--bg-primary)] transition-colors"
                      >
                        <div className="col-span-2 sm:col-span-4 min-w-0" role="cell">
                          <div className="font-medium text-[var(--text-primary)] truncate">{row.itemTitle}</div>
                          <div className="text-sm text-[var(--text-secondary)] truncate">Rented by {row.renterName}</div>
                        </div>
                        <div className="col-span-2 sm:col-span-3 text-sm text-[var(--text-secondary)]" role="cell">
                          {formatDate(row.startDate)} – {formatDate(row.endDate)}
                        </div>
                        <div className="col-span-1 order-4 sm:order-none sm:col-span-2 text-right font-semibold text-[var(--text-primary)]" role="cell">
                          {usd(row.amount)}
                        </div>
                        <div className="col-span-1 order-3 sm:order-none sm:col-span-3" role="cell">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
                            {badge.label}
                          </span>
                          <div className="text-xs text-[var(--text-secondary)] mt-1">{payoutDetail(row)}</div>
                        </div>
                      </Link>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  )
}
